import React, {useCallback, useRef, useState} from "react";
import {Box, Tooltip} from "@mui/material";
import {DragIndicator} from "@mui/icons-material";
import {Grey5} from "../../theme/styles";

const LINK = "#1976D2";

/**
 * A side panel the reader can widen, narrow, or put away.
 *
 * WHY IT IS SHARED. Two panels want exactly this -- the record rail beside a detail page, and the
 * Filter By sidebar beside a list -- and the behaviour is all edge cases: window-bound listeners,
 * a collapse threshold below the minimum, a cursor held on the body, state that survives a remount.
 * Written twice it would be right twice and then drift, and the second copy is the one nobody
 * remembers to fix.
 *
 * WHY EITHER PANEL NEEDS RESIZING AT ALL. Both hold text whose length the product does not choose.
 * The rail lists record names; the filter sidebar lists data domains, categories and other
 * user-defined classifications, nested a further 12px a level. A width tuned against the labels we
 * ship -- Draft, Released, Superseded -- is the right default and cannot be right for a customer's
 * "Post-Market Surveillance / Device Specifications". Truncation with a hover title is the fallback;
 * being able to widen the panel is the fix.
 */

type Options = {
    /** sessionStorage prefix. Width and collapsed state are kept under "<key>.width" / ".collapsed". */
    storageKey: string;
    defaultWidth: number;
    minWidth: number;
    maxWidth: number;
    /** Dragged narrower than this, the panel closes rather than becoming a column too thin to read. */
    collapseAt: number;
    /** Which side of the content the panel sits on. A right-hand panel resizes from its LEFT edge,
     *  so the drag runs the other way -- computing it as though it were on the left makes the
     *  panel shrink when the pointer says widen, which reads as the handle being broken. */
    side?: "left" | "right";
};

export function useResizablePanel({storageKey, defaultWidth, minWidth, maxWidth, collapseAt,
                                   side = "left"}: Options) {
    const widthKey = `${storageKey}.width`;
    const collapsedKey = `${storageKey}.collapsed`;

    // WIDTH AND OPEN-STATE SURVIVE NAVIGATION, which is the point of persisting them. Clicking a
    // record in the rail remounts it, so without this every click would restore a width somebody
    // had just dragged -- the control would appear not to work.
    const [width, setWidth] = useState<number>(() => {
        const saved = Number(sessionStorage.getItem(widthKey));
        return saved >= minWidth && saved <= maxWidth ? saved : defaultWidth;
    });
    const [collapsed, setCollapsed] = useState<boolean>(
        () => sessionStorage.getItem(collapsedKey) === "true");

    const panelRef = useRef<HTMLDivElement | null>(null);
    const dragging = useRef(false);

    const setCollapsedPersisted = useCallback((next: boolean) => {
        setCollapsed(next);
        sessionStorage.setItem(collapsedKey, String(next));
    }, [collapsedKey]);

    // DRAG ON THE EDGE THAT ACTUALLY MOVES. Listeners go on the WINDOW rather than the handle: a
    // pointer travelling faster than React re-renders leaves the handle behind, and a handle-bound
    // mousemove then stops receiving events mid-drag, stranding the panel at whatever width it had
    // reached.
    const onGripperDown = useCallback((event: React.MouseEvent) => {
        event.preventDefault();   // or the browser starts selecting text instead of dragging
        dragging.current = true;

        // Measured from the panel's own FIXED edge, not the viewport's: it sits beside whatever
        // the page puts before it, and assuming x === width would jump on first drag. The fixed
        // edge is the one the panel is anchored by -- its left when it sits on the left, its right
        // when it sits on the right -- because that is the edge the drag is not moving.
        const box = panelRef.current?.getBoundingClientRect();
        const anchor = side === "right" ? (box?.right ?? 0) : (box?.left ?? 0);

        const move = (e: MouseEvent) => {
            if (!dragging.current) return;
            const next = side === "right" ? anchor - e.clientX : e.clientX - anchor;

            if (next < collapseAt) {
                setCollapsed(true);
                sessionStorage.setItem(collapsedKey, "true");
                return;
            }
            const clamped = Math.max(minWidth, Math.min(maxWidth, next));
            setCollapsed(false);
            sessionStorage.setItem(collapsedKey, "false");
            setWidth(clamped);
            sessionStorage.setItem(widthKey, String(clamped));
        };

        const up = () => {
            dragging.current = false;
            window.removeEventListener("mousemove", move);
            window.removeEventListener("mouseup", up);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };

        window.addEventListener("mousemove", move);
        window.addEventListener("mouseup", up);
        // Held on the BODY for the duration: without it the cursor reverts the moment the pointer
        // leaves the handle, which during a drag is immediately.
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
    }, [collapseAt, collapsedKey, maxWidth, minWidth, side, widthKey]);

    return {width, collapsed, setCollapsed: setCollapsedPersisted, panelRef, onGripperDown};
}

/**
 * The handle: a hairline, with the grip riding on it.
 *
 * A TEN-PIXEL STRIP IS A COLUMN, and three of them across a page reads as chrome. The divider is
 * the 1px rule the rest of the app uses between panes; the grip is a small tab laid OVER it,
 * overhanging into the content on its right, so the affordance is visible without the separator
 * costing any width. The tab is the only part that has to be hittable, so it is the only part that
 * is bigger than a line -- and it is centred vertically, where a hand goes looking for it.
 *
 * POINTER EVENTS ON THE TAB ONLY. The strip is 1px and positioned relative; the tab sticks out over
 * the neighbouring content, and anything it overhangs must stay clickable. Hence pointerEvents none
 * on the wrapper and auto on the tab.
 *
 * A CLICK OPENS IT AGAIN. Dragging shut is easy to do by accident, and a collapsed panel whose only
 * way back is to guess that a hairline is draggable is a panel people lose. Clicking without moving
 * toggles it; dragging from collapsed still works.
 */
export function ResizeGripper({collapsed, onMouseDown, setCollapsed, openLabel, marker = false,
                               side = "left"}: {
    collapsed: boolean;
    onMouseDown: (event: React.MouseEvent) => void;
    setCollapsed: (next: boolean) => void;
    openLabel: string;
    /** Show a dot, for a panel whose closed state hides something the reader needs to know. */
    marker?: boolean;
    /** Which side the panel it belongs to sits on. The tab overhangs INTO the content, so it
     *  points right for a left-hand panel and left for a right-hand one. */
    side?: "left" | "right";
}) {
    const downAt = useRef<number | null>(null);

    return (
        <Box sx={{
                 width: "1px", flexShrink: 0, position: "relative", backgroundColor: "#E4E7EC",
                 // HIDDEN WITH THE PANEL IT RESIZES. Both panels are md-and-up; a handle that
                 // survived below that breakpoint would resize something nobody can see.
                 display: {xs: "none", md: "block"},
                 pointerEvents: "none",
             }}>
            <Tooltip title={collapsed ? openLabel : "Drag to resize"}
                     placement={side === "right" ? "left" : "right"}>
                <Box onMouseDown={(event: React.MouseEvent) => {
                         downAt.current = event.clientX;
                         onMouseDown(event);
                     }}
                     onMouseUp={(event: React.MouseEvent) => {
                         // A CLICK, not a drag: the pointer went down and up in the same place.
                         if (downAt.current !== null && Math.abs(event.clientX - downAt.current) < 3) {
                             setCollapsed(!collapsed);
                         }
                         downAt.current = null;
                     }}
                     sx={{
                         // FLUSH WITH THE LINE, EXTENDING RIGHT -- it lays over the content beside
                         // it rather than straddling the divider, so the panel's own edge stays a
                         // clean 1px and the tab reads as attached to it.
                         position: "absolute", top: "50%", transform: "translateY(-50%)",
                         ...(side === "right" ? {right: 0} : {left: 0}),
                         width: 9, height: 34,
                         borderRadius: side === "right" ? "3px 0 0 3px" : "0 3px 3px 0",
                         border: "1px solid #E4E7EC",
                         ...(side === "right" ? {borderRight: "none"} : {borderLeft: "none"}),
                         backgroundColor: "#FAFBFC", cursor: "col-resize", pointerEvents: "auto",
                         display: "flex", alignItems: "center", justifyContent: "center",
                         "&:hover": {backgroundColor: "#EDEFF3"},
                     }}>
                    {marker
                        ? <Box sx={{width: 4, height: 4, borderRadius: "50%", backgroundColor: LINK}}/>
                        : <DragIndicator sx={{fontSize: 12, color: Grey5}}/>}
                </Box>
            </Tooltip>
        </Box>
    );
}
